import { useTranslation, Trans } from 'react-i18next';
import { Box, Card, withStyles, useForm } from 'vopay';
import styles from './ChangePassword.module.scss';
import { buildChangePasswordRows } from '@/components/Users/ChangeUserPassword/buildChangePasswordRows.helper';
import { Button } from 'UI';
import PasswordRules from '@/components/Users/ChangeUserPassword/PasswordRules';
import { useClientLogin } from '@/providers/ClientLogin.provider';
import usePostPortalClientLogin from '@/hooks/useApi/usePostPortalClientLogin';
import useChangePortalPassword from '@/hooks/useApi/useChangePortalPassword';
import { useDataFetchIndicator } from '@/hooks';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useHistory } from 'react-router-dom';

type ChangePasswordData = {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
};

const cn = withStyles(styles);

const ChangePasswordForm: React.FC = () => {
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const { t } = useTranslation('translations', { keyPrefix: 'Users.UsersList.ChangePassword' });
  const {
    response: { LoginUserName },
  } = useClientLogin();
  const alertDialog = useAlertDialog();
  const history = useHistory();
  const [, setPending] = useDataFetchIndicator();
  const [, , , postCurrentPassword] = usePostPortalClientLogin({});
  const { changePassword } = useChangePortalPassword();

  const changePasswordRow = buildChangePasswordRows();
  const defaultValues = {
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    shouldSendWelcomeEmail: true,
  };
  const { renderFormElements, ctx } = useForm<ChangePasswordData>({
    config: changePasswordRow,
    initialValues: defaultValues,
    enableReinitialize: true,
    onSubmit: async (serializedData, { resetForm }) => {
      try {
        setPending(true);
        const loginRes = await postCurrentPassword({
          Username: LoginUserName,
          Password: serializedData.currentPassword,
        });
        if (!loginRes.Success) throw new Error(loginRes.ErrorMessage);
        const changePasswordRes = await changePassword({
          Username: LoginUserName || '',
          OldPassword: serializedData.currentPassword || '',
          NewPassword: serializedData.newPassword,
        });
        if (!changePasswordRes.Success) throw new Error(changePasswordRes.ErrorMessage);
        setPending(false);
        const closeType = await alertDialog.success(t('Alert.success'), t('Alert.newPassword'), {
          confirmLabel: tGlobal('Buttons.goDashboard'),
        });
        if (closeType === 'confirm') {
          history.push('/');
        }
      } catch (err) {
        setPending(false);
        console.error(err);
        alertDialog.error(
          <Trans i18nKey="Users.UsersList.ChangePassword.Alert.error" ns="translations" components={{ br: <br /> }} />,
          t('Alert.currentPassword'),
          {
            abortLabel: tGlobal('Buttons.close'),
          },
        );
      } finally {
        setPending(false);
        resetForm();
      }
    },
  });

  const { newPassword } = ctx.values;

  const rules = [
    { test: /[A-Z]/.test(newPassword), label: t('PasswordChecker.uppercase') },
    { test: /\d/.test(newPassword), label: t('PasswordChecker.number') },
    { test: newPassword.length >= 8, label: t('PasswordChecker.character') },
    { test: /[a-z]/.test(newPassword), label: t('PasswordChecker.lowercase') },
    { test: /[^\w]/.test(newPassword), label: t('PasswordChecker.specialCharacter') },
  ];

  const isPasswordValid = rules.every((r) => r.test);

  return (
    <div>
      <form className={cn('formContainer')} onSubmit={ctx.handleSubmit}>
        <Box flx="col" gapSize="24px">
          <Card
            hoverEffect={false}
            style={{
              padding: '24px',
              boxShadow: '0 10px 34px 0 rgba(75, 25, 25, 0.05)',
            }}
          >
            <div className={cn('fieldContainer')}>{renderFormElements({ config: changePasswordRow })}</div>
            <Card
              hoverEffect={false}
              style={{
                dropShadow: 'none',
                padding: '24px',
                backgroundColor: '#fbfbfb',
                border: '1px solid rgba(45, 45, 45, 0.10)',
              }}
            >
              <PasswordRules rules={rules} title={t('PasswordChecker.title')} />
            </Card>
          </Card>
          <div className={cn('btnContainer')}>
            <Button type="submit" colorScheme="primary" size="standard" disabled={!ctx.isValid || !isPasswordValid}>
              {t('Button.changePassword')}
            </Button>
          </div>
        </Box>
      </form>
    </div>
  );
};

export default ChangePasswordForm;
